// packages/editor/src/slot-grammar/parse-slot.ts
//
// Parses an mdast directive node (textDirective | containerDirective)
// into a structured SlotConfig. The bracket label `[name]` is at
// node.children[0].value (textDirective) or
// node.children[0].children[0].value (containerDirective wrapped in
// the directiveLabel paragraph). Attributes are flat string→string;
// we coerce numeric fields and split slash into an array.

import { SlotConfigSchema, type SlotConfig } from './slot-types';
import { bracketLabelOf } from '@rmint/core/directives/bracket-label';

function coerceInt(v: string | undefined): number | undefined {
  if (v === undefined) return undefined;
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : undefined;
}

export function parseSlot(node: any): SlotConfig {
  const isSlot =
    (node?.type === 'textDirective' ||
      node?.type === 'leafDirective' ||
      node?.type === 'containerDirective') &&
    node?.name === 'slot';
  if (!isSlot) throw new Error('expected slot directive, got ' + node?.name);

  const name = bracketLabelOf(node);
  if (!name) throw new Error('slot name required (bracket label missing)');

  const attrs = (node.attributes ?? {}) as Record<string, string>;

  const raw = {
    name,
    schema: attrs.schema ?? 'Markdown',
    source: attrs.source ?? 'human',
    reviewer: attrs.reviewer ?? 'none',
    trigger: attrs.trigger,
    slash: attrs.slash ? attrs.slash.split(',').map((s) => s.trim()).filter(Boolean) : [],
    maxWords: coerceInt(attrs.maxWords),
    maxBytes: coerceInt(attrs.maxBytes),
    maxItems: coerceInt(attrs.maxItems),
    default: attrs.default,
  };

  return SlotConfigSchema.parse(raw);
}
